import { Injectable, inject } from '@angular/core';
import {
  Address,
  createAddress,
  IGeschaeftsPartner,
  IOrderDetailsInfo,
  IPartner,
  IPersonalData,
  IPhysischePerson,
  IProduktDetails,
  IRelocationDataDTO,
  IRelocationDTOV2,
  Product,
  toReferrencedFiles,
} from '@mwe/models';
import {
  ProductCategoryEnum,
  RELOCATION_ERROR_ROUTE,
  RELOCATION_NEW_ADDRESS_ROUTE,
  RELOCATION_ROUTE,
  RELOCATION_STATUS_ROUTE,
} from '@mwe/constants';
import { Router } from '@angular/router';
import {
  getFromOlavKey,
  isProductCategory,
  isProductCategoryFernwaerme,
  mapAddressToCustomerAdressDetails,
  sortProductsEnumCategory,
} from '@mwe/utils';
import { RelocationStateService } from './relocation-state.service';
import { AddressGroupService } from '../address/address-group.service';
import { AccountLogic } from '../account/account.logic';

@Injectable({
  providedIn: 'root',
})
export class RelocationLogic {
  private readonly relocationStateService = inject(RelocationStateService);
  private readonly router = inject(Router);
  private readonly addressGroupService = inject(AddressGroupService);
  private readonly accountLogic = inject(AccountLogic);

  initRelocationStatusProcess(address: Address): void {
    this.router.navigate([`/${RELOCATION_ROUTE}/${RELOCATION_STATUS_ROUTE}`]).then(() => {
      // to prevent state-clear from wizard/ngOnDestroy
      this.relocationStateService.relocationFromAddress = createAddress(address);
    });
  }

  async initRelocationProcess(products: Product[]): Promise<void> {
    const address = createAddress(products[0].address);
    this.relocationStateService.relocationFromAddress = address;
    this.relocationStateService.relocationFromProducts = products;
    const mappedAddresses = await this.addressGroupService.loadAndMapGroupNames([address]);
    this.relocationStateService.addressDetailsLinkType = mappedAddresses?.[0]?.addressGroup?.detailsLinkType;
    this.relocationStateService.relocationInitializedForFromAddress = true;
    const url = this.checkIfRelocationIsAllowed(products) ? RELOCATION_NEW_ADDRESS_ROUTE : RELOCATION_ERROR_ROUTE;
    this.router.navigate([`/${RELOCATION_ROUTE}/${url}`]);
  }

  checkIfRelocationIsAllowed(products: Product[]): boolean {
    if (!Array.isArray(products) || products.length <= 1) {
      return true;
    }

    const stromGasArray = products.filter(
      product =>
        isProductCategory(product.category, ProductCategoryEnum.STROM) || isProductCategory(product.category, ProductCategoryEnum.GAS),
    );

    const fernwaermeArray = products.filter(product => isProductCategory(product.category, ProductCategoryEnum.FERNWAERME));

    return this.isThereOnlyOneUniqueCustomer(stromGasArray) && this.isThereOnlyOneUniqueCustomer(fernwaermeArray);
  }

  isThereOnlyOneUniqueCustomer(products: Product[]): boolean {
    if (!Array.isArray(products) || products.length <= 1) {
      return true;
    }

    const customerNumbers = products.map(product => product.customerNumber);
    const uniqueCustomerNumbers = Array.from(new Set(customerNumbers));

    return uniqueCustomerNumbers.length === 1;
  }

  getUniqueCustomerNumbers(): string[] {
    return [...new Set(this.relocationStateService.relocationDetails.map(rd => rd.customerNumber + '_' + rd.accountNumber))];
  }

  getUniqueCustomerNumbersWithStatus(): string[] {
    const customerAccountNumbers = this.getUniqueCustomerNumbers().toSorted((a, b) => (a < b ? -1 : 1));
    const moveOutAccounts: string[] = [];
    const newClientAccounts: string[] = [];
    const newClientGpIds: string[] = [];
    for (const caNumber of customerAccountNumbers) {
      const ids = caNumber.split('_');
      let details = this.relocationStateService.relocationDetails.filter(
        rd => rd.customerNumber === ids[0] && rd.accountNumber === ids[1] && rd.status === 'Abmeldung',
      );
      if (details?.length > 0) {
        moveOutAccounts.push(caNumber + '_Abmeldung_' + details.map(d => d.category).join(','));
      }
      if (!newClientGpIds.includes(ids[0])) {
        newClientGpIds.push(ids[0]);
        if (Number.isNaN(Number(ids[0]))) {
          details = this.relocationStateService.relocationDetails.filter(
            rd => Number.isNaN(Number(rd.customerNumber)) && rd.status === 'Anmeldung',
          );
        } else {
          details = this.relocationStateService.relocationDetails.filter(rd => rd.customerNumber === ids[0] && rd.status === 'Anmeldung');
        }
        const newProducts = sortProductsEnumCategory(details.map(d => d.category));
        for (const category of newProducts) {
          newClientAccounts.push(ids[0] + '_Anmeldung_' + category);
        }
      }
    }
    return [...moveOutAccounts, ...newClientAccounts];
  }

  getRelocationDTOV2(): IRelocationDTOV2 {
    const mappingDataIds = this.getUniqueCustomerNumbersWithStatus();
    const defaultIBAN = this.relocationStateService.relocationDetails.find(rd => rd.bezahlartIcon === 'sepa' && rd.status === 'Abmeldung')
      ?.verrechnungskonto?.eingangskonto.iban;
    const defaultERechnugsEmail = this.relocationStateService.relocationDetails.find(rd => rd.eRechnungInfo && rd.status === 'Abmeldung')
      ?.eRechnungInfo?.rechnungEmail;
    const mappingData = mappingDataIds.map((dataIds, index) => {
      let moveOutInfo: IOrderDetailsInfo = null;
      let newClientInfo: IOrderDetailsInfo = null;
      const ids = dataIds.split('_');
      if (dataIds.includes('Abmeldung')) {
        moveOutInfo = this.relocationStateService.relocationDetails.find(
          rd => rd.customerNumber === ids[0] && rd.accountNumber === ids[1] && rd.status === 'Abmeldung',
        );
      }
      const categories = ids.at(-1).split(',');
      if (dataIds.includes('Anmeldung')) {
        newClientInfo = this.relocationStateService.relocationDetails.find(
          rd => rd.category === categories[0] && rd.status === 'Anmeldung',
        );
      }

      const partnerData = this.getPartnerData(moveOutInfo, newClientInfo, defaultERechnugsEmail);

      const customerNumber = Number.isNaN(Number(ids[0])) ? null : ids[0];
      const isuTariffKey = newClientInfo?.tariff.shortcut || moveOutInfo?.tariff.name;
      return {
        orderIdx: index,
        customerNumber: customerNumber,
        accountNumber: moveOutInfo ? ids[1] : null,
        businessPartnerNumber: customerNumber,
        categories: categories,
        isuTariffKeys: [isuTariffKey],
        tariffKeys: [newClientInfo?.tariff.key],
        tariffName: newClientInfo?.tariff.name,
        tariffClasses: moveOutInfo ? [moveOutInfo.availabilityDetailsFernwaerme] : [this.getAvailabilitiesForFWProduct(newClientInfo)],
        tarifOptionen: newClientInfo?.tarifOptionen.filter(to => to.default),
        condition: newClientInfo?.condition,
        zustelladresse: newClientInfo?.zustelladresse,
        personalData: partnerData,
        invoiceAddress: mapAddressToCustomerAdressDetails(newClientInfo?.invoiceAddress || this.relocationStateService.relocationToAddress),
        changeERechnung: !!defaultERechnugsEmail,
        changeSepaMandat: !!defaultIBAN,
        processAdvice: ids.at(-2),
      };
    });

    return {
      oldAddress: this.relocationStateService.relocationFromAddress,
      newAddressOlav: this.relocationStateService.relocationToOlav,
      deregistrationDate: this.relocationStateService.deregistrationDate,
      registrationDate: this.relocationStateService.registrationDate,
      idCard: this.relocationStateService.idCard,
      referencedFiles: this.relocationStateService.uploadedFiles
        ? toReferrencedFiles(this.relocationStateService.uploadedFiles.uploadedFiles)
        : null,
      pdfLinks: this.relocationStateService.pdfLinks,
      priceIndications: this.relocationStateService.priceIndications,
      mappingData: mappingData,
      eRechnungsInfo: defaultERechnugsEmail ? { isERechnungsEmpfaenger: true, rechnungEmail: defaultERechnugsEmail } : null,
      sepaMandat: defaultIBAN ? { iban: defaultIBAN } : null,
      noOnlineCategories: this.relocationStateService.noOnlineCategories,
      noLongerAvailableCategories: this.relocationStateService.noLongerAvailableCategories,
      version: 'V2',
    };
  }

  private getAvailabilitiesForFWProduct(newClientInfo: IOrderDetailsInfo) {
    if (isProductCategoryFernwaerme(newClientInfo?.category)) {
      const fwDetails = this.relocationStateService.relocationDetails.find(rd => rd.category === newClientInfo.category);
      return fwDetails?.availabilityDetailsFernwaerme;
    }
    return null;
  }

  private getPartnerData(moveOutInfo: IOrderDetailsInfo, newClientInfo: IOrderDetailsInfo, defaultERechnugsEmail) {
    const partner = moveOutInfo?.produktDetails.partner || newClientInfo?.produktDetails.partner;
    const birthday = moveOutInfo?.physischePerson?.geburtsdatum || newClientInfo?.physischePerson?.geburtsdatum;
    return partner
      ? this.getPersonalDataFromPartner(partner, defaultERechnugsEmail || this.accountLogic.getAccountEmail(), birthday)
      : null;
  }

  getPersonalDataFromPartner = (partnerDetail: IPartner, email: string, birthday: string): IPersonalData => {
    return {
      salutation: partnerDetail.anrede,
      title: { text: partnerDetail?.titel, value: '' },
      firstName: partnerDetail.vorname,
      lastName: partnerDetail.nachname,
      birthday: birthday?.replace('T00:00Z-', ''),
      telefonNumber: partnerDetail.telefon,
      telefonCountryCode: partnerDetail.telefonvorwahl,
      email: partnerDetail.email || email,
    };
  };

  getProductsForNoOnlineCategories(orderDetails: IOrderDetailsInfo[], noOnlineCategories: string[]): Product[] {
    const noOnlineProducts = [];
    if (noOnlineCategories) {
      for (const od of orderDetails) {
        if (noOnlineCategories.includes(od.category) && od.status === 'Abmeldung') {
          noOnlineProducts.push({ category: od.category, tariffClasses: [od.tariff?.name] });
        }
      }
    }
    return noOnlineProducts;
  }

  public parseVersionTwo(relocationData: any, relocationDetails: any) {
    const dto: IRelocationDataDTO = {
      selectedOlav: relocationData?.newAddressOlav,
      oldAddress: createAddress(relocationData?.oldAddress),
      newAddress: getFromOlavKey(relocationData?.newAddressOlav),
      deregistrationDate: relocationData?.deregistrationDate,
      registrationDate: relocationData?.registrationDate,
      relocationSubmittedAt: relocationDetails?.createdAt,
      idcard: relocationData?.idCard,
      referencedFiles: relocationData?.referencedFiles,
      useSubstituteDetails: relocationData?.useSubstituteDetails,
      pdfLinks: relocationData?.pdfLinks,
      noOnlineCategories: relocationData?.noOnlineCategories,
      noLongerAvailableCategories: relocationData?.noLongerAvailableCategories,
      version: relocationDetails.version,
    } as IRelocationDataDTO;

    this.createOrderDetailsInfo(relocationData, dto);

    dto.oldAddress.addressGroup.id = relocationDetails.addressGroupId;
    return dto;
  }

  private createOrderDetailsInfo(relocationData: any, dto: IRelocationDataDTO) {
    const orderDetailsInfo: IOrderDetailsInfo[] = [];
    for (const md of relocationData.mappingData) {
      for (const category of md.categories) {
        orderDetailsInfo.push({
          status: md.processAdvice,
          accountNumber: md.accountNumber,
          customerNumber: md.customerNumber,
          category: category,
          apiVersion: md.version,
          tariff: md.isuTariffKeys?.[0] ? { shortcut: md.isuTariffKeys[0], key: md.tariffKeys[0], name: md.tariffName } : null,
          tarifOptionen: md.tarifOptionen,
          availabilityDetailsFernwaerme: md.tariffClasses?.[0],
          eRechnungInfo:
            relocationData.eRechnungsInfo?.isERechnungsEmpfaenger === true
              ? relocationData.eRechnungsInfo
              : { isERechnungsEmpfaenger: false },
          bezahlartIcon: relocationData.sepaMandat ? 'sepa' : 'zahlschein',
          bezahlartText: relocationData.sepaMandat?.iban || 'zahlschein',
          partnerDetails: this.getPartnerDetails(md),
          produktDetails: this.getProducktDetails(md),
          condition: md.condition,
          zustelladresse: md.zustelladresse,
          physischePerson: {
            geburtsdatum: md.personalData?.birthday,
            anrede: md.personalData?.salutation,
            vorname: md.personalData?.firstName,
            nachname: md.personalData?.lastName,
          },
        });
      }
    }
    dto.orderDetailsInfo = orderDetailsInfo;
  }

  private getProducktDetails(md) {
    return {
      partner: {
        partnerId: md.customerNumber,
        anrede: md.personalData?.salutation,
        vorname: md.personalData?.firstName,
        nachname: md.personalData?.lastName,
        email: md.personalData?.email,
        telefon: md.personalData?.telefonNumber,
        telefonvorwahl: md.personalData?.telefonCountryCode,
      } as IPartner,
    } as IProduktDetails;
  }

  private getPartnerDetails(md) {
    return {
      geburtsDatum: md.personalData?.birthday,
      physischePerson: {
        anrede: md.personalData?.salutation,
        vorname: md.personalData?.firstName,
        nachname: md.personalData?.lastName,
        email: md.personalData?.email,
        telefon: md.personalData?.telefonNumber,
        telefonvorwahl: md.personalData?.telefonCountryCode,
      } as IPhysischePerson,
    } as IGeschaeftsPartner;
  }

  public parseVersionOne(relocationData, relocationDetails: any) {
    const dto: IRelocationDataDTO = {
      selectedOlav: relocationData?.selectedOlav,
      oldAddress: createAddress(relocationData?.moveFromAddress ? relocationData?.moveFromAddress : relocationData?.oldAddress),
      newAddress: createAddress(relocationData?.moveToAddress ? relocationData?.moveToAddress : relocationData?.newAddress),
      deregistrationDate: relocationData?.deregistrationAt ? relocationData?.deregistrationAt : relocationData?.deregistrationDate,
      registrationDate: relocationData?.registrationAt ? relocationData?.registrationAt : relocationData?.registrationDate,
      relocationSubmittedAt: relocationDetails?.createdAt,
      orderDetailsInfo: relocationData?.relocationDetailsInfo ? relocationData?.relocationDetailsInfo : relocationData?.orderDetailsInfo,
      idcard: relocationData?.idcard,
      referencedFiles: relocationData?.referencedFiles,
      useSubstituteDetails: relocationData?.useSubstituteDetails,
      pdfLinks: relocationData?.pdfLinks,
      noOnlineCategories: relocationData?.noOnlineCategories,
    };
    dto.oldAddress.addressGroup.id = relocationDetails.addressGroupId;
    return dto;
  }
}
